// Registry of photo-upload targets — one entry per record type that plugs into
// the shared offline photo queue (lib/photoFlusher.js, hooks/useOfflinePhotoQueue.js).
// Every target describes how to build the upload request and (optionally) how to
// report/locate the record for a given entity type. Adding a new screen to the
// offline pipeline means adding ONE entry here — no changes needed to the queue,
// compression, backoff or device-wide panel code.
export const PHOTO_UPLOAD_TARGETS = {
    form_instance: {
        label: "Audits & Inspections",
        uploadUrl: (entityId) => `/forms/instances/${entityId}/upload`,
        buildFormData: (meta, blob, filename, clientUploadId) => {
            const fd = new FormData();
            fd.append("file", blob, filename || "photo.jpg");
            if (meta.field_id) fd.append("field_id", meta.field_id);
            if (meta.section_id) { fd.append("section_id", meta.section_id); fd.append("anchor", meta.anchor || "bottom"); }
            if (meta.row_id) fd.append("row_id", meta.row_id);
            if (clientUploadId) fd.append("client_upload_id", clientUploadId);
            return fd;
        },
        pendingUrl: (entityId) => `/forms/instances/${entityId}/pending-photos`,
        eventUrl: (entityId) => `/forms/instances/${entityId}/photo-events`,
        recordPath: (entityId) => `/app/inspections/${entityId}`,
    },
    weekly_report: {
        label: "Weekly Site Report",
        uploadUrl: (entityId) => `/weekly-reports/${entityId}/photos`,
        buildFormData: (meta, blob, filename, clientUploadId) => {
            const fd = new FormData();
            fd.append("file", blob, filename || "photo.jpg");
            if (meta.caption) fd.append("caption", meta.caption);
            if (clientUploadId) fd.append("client_upload_id", clientUploadId);
            return fd;
        },
        pendingUrl: (entityId) => `/weekly-reports/${entityId}/pending-photos`,
        recordPath: (entityId) => `/app/weekly-reports/${entityId}`,
    },
    incident: {
        label: "Accident / Incident",
        uploadUrl: (entityId) => `/incidents/${entityId}/upload`,
        buildFormData: (meta, blob, filename, clientUploadId) => {
            const fd = new FormData();
            fd.append("file", blob, filename || "photo.jpg");
            if (meta.caption) fd.append("caption", meta.caption);
            if (clientUploadId) fd.append("client_upload_id", clientUploadId);
            return fd;
        },
        pendingUrl: (entityId) => `/incidents/${entityId}/pending-photos`,
        recordPath: (entityId) => `/app/incidents?open=${entityId}`,
    },
    asbestos_zone: {
        label: "Asbestos record",
        uploadUrl: (entityId) => `/asbestos/zones/${entityId}/photos`,
        buildFormData: (meta, blob, filename, clientUploadId) => {
            const fd = new FormData();
            fd.append("file", blob, filename || "photo.jpg");
            if (meta.caption) fd.append("caption", meta.caption);
            if (clientUploadId) fd.append("client_upload_id", clientUploadId);
            return fd;
        },
        recordPath: (entityId, meta) => meta?.buildingId ? `/app/buildings/${meta.buildingId}/asbestos` : undefined,
    },
    tmp: {
        label: "Traffic Management Plan",
        uploadUrl: (entityId) => `/tmp/${entityId}/images`,
        buildFormData: (meta, blob, filename, clientUploadId) => {
            const fd = new FormData();
            fd.append("file", blob, filename || "photo.jpg");
            fd.append("section_key", meta.section_key);
            if (meta.caption) fd.append("caption", meta.caption);
            if (clientUploadId) fd.append("client_upload_id", clientUploadId);
            return fd;
        },
        recordPath: (entityId) => `/app/tmp/${entityId}`,
    },
    cpp_section: {
        label: "Construction Phase Plan",
        uploadUrl: (entityId) => { const [recordId, code] = entityId.split("::"); return `/cpp/${recordId}/sections/${code}/images`; },
        buildFormData: (meta, blob, filename, clientUploadId) => {
            const fd = new FormData();
            fd.append("file", blob, filename || "photo.jpg");
            if (meta.label) fd.append("label", meta.label);
            if (clientUploadId) fd.append("client_upload_id", clientUploadId);
            return fd;
        },
        recordPath: (entityId) => { const [recordId] = entityId.split("::"); return `/app/cpp/${recordId}`; },
    },
    rams_request: {
        label: "RAMS questionnaire",
        uploadUrl: (entityId) => `/rams/requests/${entityId}/upload`,
        buildFormData: (meta, blob, filename, clientUploadId) => {
            const fd = new FormData();
            fd.append("file", blob, filename || "photo.jpg");
            fd.append("field_key", meta.field_key);
            if (clientUploadId) fd.append("client_upload_id", clientUploadId);
            return fd;
        },
        recordPath: () => `/app/rams`,
    },
    rams_public: {
        label: "RAMS questionnaire (external)",
        uploadUrl: (entityId) => `/public/rams/${entityId}/upload`,
        buildFormData: (meta, blob, filename, clientUploadId) => {
            const fd = new FormData();
            fd.append("file", blob, filename || "photo.jpg");
            fd.append("field_key", meta.field_key);
            if (clientUploadId) fd.append("client_upload_id", clientUploadId);
            return fd;
        },
        // No recordPath — a public/token link is meaningless to navigate to from the internal app.
    },
};

export function getUploadTarget(entityType) {
    return PHOTO_UPLOAD_TARGETS[entityType];
}

export function registerUploadTarget(entityType, target) {
    PHOTO_UPLOAD_TARGETS[entityType] = target;
}
